/**
 * Text slider - two designs under one name, told apart by `layout`.
 *
 * **`cards`** - Figma `6788:23339`, on Opieka klienta (it was `6377:87505`, an expanding row,
 * until 2026-11-03). Three cards - previous, featured in blue, next - and arrows that go
 * round for ever. See `CardRow`.
 *
 * **`photo`** - Figma `6377:88433`, `6377:88443` and `6377:88444`, on Zrownowazony rozwoj.
 * One slide at a time in a 1360x1057 block: the slide's photograph on the top half, a
 * tinted panel on the bottom half with the slide's heading in the top corner, its text
 * bottom right and previous/next bottom left. The three frames are three slides, each
 * with its own photograph and its own colour. Until 2026-09-26 the page drew the photo as
 * a separate `hero-media` above a `cards` row, which is the contradiction the inventory
 * carried until the designer settled it.
 *
 * **`shared.slide` gained `heading`, and later `media`.** The component held `text`,
 * `author` and `role` - quote fields - and the cards need a short line for the collapsed
 * state and a longer one for the open state; the photo layout needs a picture per slide.
 * `author` and `role` are untouched for quote-style use.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { SectionShell } from '../primitives';
import type { Slide, TextSliderSection } from '../../types/sections';
import { Icon } from '../../components/ui/Icon';
import { SIZES } from '../../components/ui/Image';
import { MediaFrame, aspectClass, hasMedia } from '../../components/ui/MediaFrame';
import { useSite } from '../../lib/site-context';

export default function TextSlider({ data }: { data: TextSliderSection }) {
  const slides = data.slides ?? [];
  if (!slides.length) return null;

  return data.layout === 'photo'
    ? <PhotoSlider data={data} slides={slides} />
    : <CardRow data={data} slides={slides} />;
}

/** Where a slot sits, in the 1360-wide frame of Figma 6788:23339, for its distance from the featured card. */
const SIDE = 294;
const FEATURED = 740;
const GAP = 16;
const geometry = (o: number) =>
  o === 0
    ? { left: SIDE + GAP, width: FEATURED }
    : o < 0
      ? { left: (o + 1) * (SIDE + GAP), width: SIDE }
      : { left: SIDE + GAP + FEATURED + GAP + (o - 1) * (SIDE + GAP), width: SIDE };
const pct = (n: number) => `${(n / 1360) * 100}%`;
const SLIDE_MS = 500;

/**
 * Figma 6788:23339 and 6788:23355: three cards in a 1360x444 row - the previous slide
 * (294), the featured one (740) and the next (294), 16 apart - with a grey round
 * previous/next pair under it. An arrow moves the window by one and goes round for ever,
 * and the move is animated: the featured card shrinks into the previous slot while the next
 * one grows into the featured.
 *
 * **How a loop animates.** Five fixed slots (two either side of the featured) are laid out
 * absolutely, each showing the slide at its distance from the current one. A move first
 * slides every slot into its neighbour's place; when that ends, the current slide changes
 * and every slot snaps back to its own place with the transition off - the same picture,
 * because each slot now shows what its neighbour did. A card never has to jump across the
 * row, so a loop of three works as well as a loop of thirty.
 *
 * A card carries both of its faces - the featured one and the side one - at fixed widths,
 * clipped by the card, so the text does not reflow while the card changes size; they cross-
 * fade. Below `lg` there is no row: the featured card alone, full width, and the arrows
 * switch at once. Fewer than three slides is a static pair or single card.
 */
function CardRow({ data, slides }: { data: TextSliderSection; slides: Slide[] }) {
  const { t } = useSite();
  const [current, setCurrent] = useState(0);
  const [sliding, setSliding] = useState<0 | 1 | -1>(0);
  const [animate, setAnimate] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const count = slides.length;
  const loop = count > 2;
  const at = (offset: number) => (((current + offset) % count) + count) % count;

  useEffect(() => () => clearTimeout(timer.current), []);

  const go = (step: 1 | -1) => {
    if (sliding) return;
    const moves = loop
      && window.matchMedia('(min-width: 1024px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!moves) {
      setAnimate(false);
      setCurrent(at(step));
      return;
    }
    setAnimate(true);
    setSliding(step);
    timer.current = setTimeout(() => {
      setAnimate(false);
      setCurrent(at(step));
      setSliding(0);
    }, SLIDE_MS);
  };

  const arrows = count > 1 ? (
    <div className="flex gap-2 text-grey-700">
      {([-1, 1] as const).map((step) => (
        <button
          key={step}
          type="button"
          onClick={() => go(step)}
          aria-label={t(step === -1 ? 'slider.previous' : 'slider.next')}
          className="flex rounded-full transition-colors hover:text-grey-900"
        >
          <Icon name="chevron-circle-right" size={48} className={step === -1 ? '-scale-x-100' : ''} />
        </button>
      ))}
    </div>
  ) : null;

  const frame = (children: ReactNode) => (
    <SectionShell settings={data.settings} name="text-slider">
      {data.heading ? <h2 className="max-w-[678px] text-h2 text-ink">{data.heading}</h2> : null}
      <div
        role="region"
        aria-roledescription={t('slider.carousel')}
        aria-label={data.heading ?? 'Slider'}
        className="flex flex-col gap-6"
      >
        {children}
        {arrows}
      </div>
    </SectionShell>
  );

  if (!loop) {
    // One or two slides: nothing to go round, so no row to animate. The featured card, and
    // the other beside it on a wide screen.
    const other = count > 1 ? slides[at(1)] : null;
    return frame(
      <div className={`grid gap-4 ${other ? 'lg:grid-cols-[740fr_294fr]' : ''}`}>
        <FeaturedFace slide={slides[current]} index={current} count={count} className="rounded-s bg-brand-500" />
        {other ? (
          <button
            type="button"
            onClick={() => go(1)}
            aria-label={`${t('slider.next')}: ${other.heading ?? ''}`}
            className="hidden min-h-[444px] flex-col justify-end rounded-s bg-surface-muted p-9 text-left text-body-md text-ink lg:flex"
          >
            {other.heading}
          </button>
        ) : null}
      </div>
    );
  }

  return frame(
    <div className="[container-type:inline-size]">
      <div className="relative lg:h-[444px] lg:overflow-hidden">
        {[-2, -1, 0, 1, 2].map((o) => {
          const slide = slides[at(o)];
          const target = o - sliding;
          const place = geometry(target);
          const featured = target === 0;
          const base = 'lg:absolute lg:inset-y-0 lg:left-[var(--l)] lg:w-[var(--w)] overflow-hidden rounded-s';
          const style = { '--l': pct(place.left), '--w': pct(place.width) } as CSSProperties;
          const motion = animate ? 'lg:transition-[left,width,background-color] lg:duration-500 lg:ease-in-out' : '';
          const tone = featured ? 'lg:bg-brand-500' : 'lg:bg-surface-muted';

          const faces = (
            <>
              <FeaturedFace
                slide={slide}
                index={at(o)}
                count={count}
                bare={o !== 0}
                className={`${featured ? '' : 'lg:opacity-0'} lg:absolute lg:inset-y-0 lg:left-0 lg:w-[54.41cqw] lg:transition-opacity lg:duration-500`}
              />
              <span
                aria-hidden
                className={`absolute inset-y-0 left-0 hidden w-[21.62cqw] flex-col justify-end p-9 text-body-md text-ink transition-opacity duration-500 lg:flex ${featured ? 'opacity-0' : ''}`}
              >
                <span className="max-w-[222px]">{slide.heading}</span>
              </span>
            </>
          );

          if (o === 0) {
            return (
              <div key={o} className={`${base} ${motion} bg-brand-500 ${tone}`} style={style}>
                {faces}
              </div>
            );
          }
          if (Math.abs(o) === 2) {
            return <div key={o} aria-hidden className={`${base} ${motion} hidden ${tone}`} style={style}>{faces}</div>;
          }
          return (
            <button
              key={o}
              type="button"
              tabIndex={sliding ? -1 : 0}
              onClick={() => go(o === -1 ? -1 : 1)}
              aria-label={`${t(o === -1 ? 'slider.previous' : 'slider.next')}: ${slide.heading ?? ''}`}
              className={`${base} ${motion} hidden text-left lg:block ${tone}`}
              style={style}
            >
              {faces}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The featured card's text: the heading top left, the paragraph at the foot. */
function FeaturedFace({ slide, index, count, className = '', bare = false }: {
  slide: Slide; index: number; count: number; className?: string; bare?: boolean;
}) {
  const { t } = useSite();
  return (
    <div
      role={bare ? undefined : 'group'}
      aria-roledescription={bare ? undefined : t('slider.slide')}
      aria-label={bare ? undefined : t('slider.position', { index: index + 1, count })}
      aria-live={bare ? undefined : 'polite'}
      aria-hidden={bare ? true : undefined}
      className={`flex min-h-[444px] flex-col justify-between gap-10 p-6 text-white lg:p-9 ${className}`}
    >
      {slide.heading ? <h3 className="text-h4">{slide.heading}</h3> : null}
      <div className="flex flex-col gap-4">
        <p className="whitespace-pre-line text-body lg:max-w-[438px]">{slide.text}</p>
        {slide.author ? (
          <p className="text-body-sm">
            {slide.author}
            {slide.role ? <span className="text-white/70">, {slide.role}</span> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The photo layout's colours, in the order the file draws its three slides: green, blue,
 * orange, then round again. Like the pillars' palette there is no colour field - the
 * design made the decision, and a field would only ask an editor to repeat it.
 *
 * Panel and text are the file's own pairs (green-50/800, blue-50/600, orange-50/700, all
 * above 5:1). The buttons are one step lighter than the text, as drawn, and still clear
 * the 3:1 a control needs against its panel.
 */
const TONES = [
  { panel: 'bg-green-50', text: 'text-green-800', control: 'text-green-700 hover:text-green-800' },
  { panel: 'bg-blue-50', text: 'text-blue-600', control: 'text-blue-500 hover:text-blue-600' },
  { panel: 'bg-orange-50', text: 'text-orange-700', control: 'text-orange-600 hover:text-orange-700' }
];

const tone = (index: number) => TONES[index % TONES.length];

function PhotoSlider({ data, slides }: { data: TextSliderSection; slides: Slide[] }) {
  const { t } = useSite();
  const headingId = useId();
  const [current, setCurrent] = useState(0);
  const count = slides.length;
  // Round and round: the file draws both arrows always on, and a slider has no end that
  // a visitor needs to be stopped at.
  const go = (step: 1 | -1) => setCurrent((i) => (i + step + count) % count);

  return (
    <SectionShell settings={data.settings} name="text-slider">
      {/* The file draws no heading here. It still names the slider for a screen reader,
          and keeps the outline from jumping from the page's h1 to the slides' h3s. */}
      {data.heading ? <h2 id={headingId} className="sr-only">{data.heading}</h2> : null}

      <div
        role="region"
        aria-roledescription={t('slider.carousel')}
        aria-labelledby={data.heading ? headingId : undefined}
        className="relative"
      >
        {/* Every slide is laid out in the same cell, so the block is as tall as its tallest
            slide and an arrow never moves the page under the visitor. `invisible` takes the
            others out of the tab order and the accessibility tree as well as out of sight,
            and the live region reads out the one that arrives.

            The arriving slide fades in on top while the leaving one stays opaque beneath it
            and only goes once the fade is over. Fading both at once let the page's white
            through the middle of every change. */}
        <div aria-live="polite" className="grid">
          {slides.map((slide, i) => {
            const active = i === current;
            const photo = hasMedia(slide.media);
            return (
              <div
                key={i}
                role="group"
                aria-roledescription={t('slider.slide')}
                aria-label={t('slider.position', { index: i + 1, count })}
                className={`col-start-1 row-start-1 flex flex-col motion-reduce:transition-none ${
                  active
                    ? 'z-10 opacity-100 transition-opacity duration-300'
                    : 'invisible z-0 opacity-0 transition-[opacity,visibility] delay-300 duration-0'
                }`}
              >
                {photo ? (
                  // Half of the block: 1360x529 at the file's width. `aspect` still wins when
                  // an editor set one - the shared cell keeps the other slides level with it.
                  <figure className={`relative m-0 overflow-hidden rounded-t-l bg-surface-sunken ${
                    aspectClass(slide.media) ?? 'aspect-[4/3] md:aspect-[1360/529]'
                  }`}>
                    <MediaFrame media={slide.media} sizes={SIZES.full} title={slide.heading ?? undefined} />
                  </figure>
                ) : null}

                {/* The other half, at least as tall as the photograph and taller when the
                    text needs it. `grow` rather than `flex-1`: a zero basis would throw the
                    aspect ratio away. The bottom padding on small screens is where the
                    buttons sit, under the text. */}
                <div className={`flex grow flex-col justify-between gap-10 p-6 pb-24 lg:aspect-[1360/529] lg:p-16 ${
                  photo ? 'rounded-b-l' : 'rounded-l'
                } ${tone(i).panel}`}>
                  {slide.heading ? <h3 className={`text-body ${tone(i).text}`}>{slide.heading}</h3> : null}
                  <p className={`text-body-md md:text-h4 lg:w-[603px] lg:self-end ${tone(i).text}`}>{slide.text}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* One pair of buttons for every slide, laid over the panel's corner. Inside each
            slide they would vanish with it and take the keyboard focus along. */}
        {count > 1 ? (
          <div className="absolute bottom-6 left-6 z-20 flex gap-2 lg:bottom-16 lg:left-16">
            {([-1, 1] as const).map((step) => (
              <button
                key={step}
                type="button"
                onClick={() => go(step)}
                aria-label={t(step === -1 ? 'slider.previous' : 'slider.next')}
                className={`flex rounded-full transition-colors ${tone(current).control}`}
              >
                <Icon name="chevron-circle-right" size={48} className={step === -1 ? '-scale-x-100' : ''} />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}
